import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/index'

// Layouts
import MainLayout from '@/layouts/MainLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'

// Auth views
import LoginView from '@/views/auth/LoginView.vue'
import Register from '@/views/auth/Register.vue'
import ForgotPasswordView from '@/views/auth/ForgotPasswordView.vue'

// Maintenance view
import MaintenanceView from '@/views/maintenance/MaintenanceView.vue'

// App views
import DashboardView from '@/views/dashboard/Dashboard.vue'
import ProductsView from '@/views/products/ProductsView.vue'
import ProductDetailView from '@/views/products/ProductDetailView.vue'
import SalesView from '@/views/sales/SalesView.vue'
import SaleSimple from '@/views/sales/SaleSimple.vue'
import SaleDiffere from '@/views/sales/SaleDiffere.vue'
import SaleDetailsView from '@/views/sales/SaleDetailsView.vue'
import SaleInvoiceDetailsView from '@/views/saleInvoices/SaleInvoiceDetailsView.vue'
import CustomerList from '@/views/customers/CustomerList.vue'
import CategoriesView from '@/views/categories/CategoriesView.vue'
import UsersView from '@/views/users/UsersView.vue'
import SettingsView from '@/views/settings/SettingsView.vue'
import ProfileView from '@/views/profile/ProfileView.vue'
import CustomerDetail from '@/views/customers/CustomerDetail.vue'
import SuppliersView from '@/views/suppliers/SuppliersView.vue'
import SupplierDetailView from '@/views/suppliers/SupplierDetailView.vue'
import ProductRegularizationsView from '@/views/products/ProductRegularizationsView.vue'
import PurchaseOrdersView from '@/views/purchaseOrders/PurchaseOrdersView.vue'
import CreatePurchaseOrderView from '@/views/purchaseOrders/CreatePurchaseOrderView.vue'
import PurchaseOrderDetailsView from '@/views/purchaseOrders/PurchaseOrderDetailsView.vue'
import SupplierInvoicesView from '@/views/supplierInvoices/SupplierInvoicesView.vue'
import SupplierInvoiceDetailsView from '@/views/supplierInvoices/SupplierInvoiceDetailsView.vue'
import CreateSupplierInvoiceView from '@/views/supplierInvoices/CreateSupplierInvoiceView.vue'
import ProformaInvoicesView from '@/views/proformaInvoices/ProformaInvoicesView.vue'
import CreateProformaInvoiceView from '@/views/proformaInvoices/CreateProformaInvoiceView.vue'

// Inventory views
import InventoryOverview from '@/views/inventory/InventoryOverview.vue'
import InventoryHistory from '@/views/inventory/InventoryHistory.vue'
import CreateInventory from '@/views/inventory/CreateInventory.vue'
import DoingInventory from '@/views/inventory/DoingInventory.vue'
import InventoryDetails from '@/views/inventory/InventoryDetails.vue'
import StockWithdrawalList from '@/views/stockWithdrawal/StockWithdrawalList.vue'
import CreateStockWithdrawalView from '@/views/stockWithdrawal/CreateStockWithdrawalView.vue'
import StockWithdrawalDetailsView from '@/views/stockWithdrawal/StockWithdrawalDetailsView.vue'
// Cash Register views
import CashRegisterHomeView from '@/views/cashRegister/CashRegisterHomeView.vue'
import CashRegistersHistory from '@/components/cashRegister/CashRegistersHistory.vue'

const routes = [
    // Route de maintenance (doit être avant toutes les autres pour bloquer la navigation)
    {
        path: '/maintenance',
        name: 'maintenance',
        component: MaintenanceView,
        meta: {
            title: 'Maintenance'
        }
    },

    // Redirection de la racine
    {
        path: '/',
        redirect: '/dashboard'
    },

    // Routes d'authentification
    {
        path: '/',
        component: AuthLayout,
        children: [
            {
                path: 'login',
                name: 'login',
                component: LoginView,
                meta: {
                    guest: true,
                    title: 'Connexion'
                }
            },
            {
                path: 'register',
                name: 'register',
                component: Register,
                meta: {
                    guest: true,
                    title: 'Inscription'
                }
            },
            {
                path: 'forgot-password',
                name: 'forgot-password',
                component: ForgotPasswordView,
                meta: {
                    guest: true,
                    title: 'Mot de passe oublié'
                }
            }
        ]
    },

    // Route standalone pour l'inventaire mobile (sans layout)
    {
        path: '/doing-inventory',
        name: 'doing-inventory',
        component: DoingInventory,
        meta: {
            requiresAuth: true,
            title: 'Inventaire en cours'
        }
    },

    // Routes protégées (application principale)
    {
        path: '/',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            // TABLEAU DE BORD
            {
                path: 'dashboard',
                name: 'dashboard',
                component: DashboardView,
                meta: { title: 'Tableau de bord' }
            },

            // MODULE VENTES & CAISSES
            {
                path: 'cash-registers',
                name: 'cash-registers',
                component: CashRegisterHomeView,
                meta: { title: 'Caisses' }
            },
            {
                path: 'cash-registers-history',
                name: 'cash-registers-history',
                component: CashRegistersHistory,
                meta: { title: 'Historique des caisses' }
            },
            {
                path: '/sales/simple',
                name: 'sale-simple',
                component: SaleSimple,
                meta: { title: 'Vente Comptant' }
            },
            {
                path: '/sales/differe',
                name: 'sale-differe',
                component: SaleDiffere,
                meta: { title: 'Vente Différée' }
            },
            {
                path: 'sales',
                name: 'sales',
                component: SalesView,
                meta: { title: 'Historique des ventes' }
            },
            {
                path: '/sales/:id',
                name: 'sale-details',
                component: SaleDetailsView,
                meta: { title: 'Détails de la vente' },
                props: true
            },
            {
                path: '/sale-invoices/:id',
                name: 'sale-invoice-details',
                component: SaleInvoiceDetailsView,
                meta: { title: 'Détails de la facture' },
                props: true
            },
            {
                path: 'proforma-invoices',
                name: 'proforma-invoices',
                component: ProformaInvoicesView,
                meta: { title: 'Factures Proforma' }
            },
            {
                path: 'proforma-invoices/new',
                name: 'create-proforma-invoice',
                component: CreateProformaInvoiceView,
                meta: { title: 'Nouvelle facture proforma' }
            },

            // MODULE STOCK & INVENTAIRE
            {
                path: 'products',
                name: 'products',
                component: ProductsView,
                meta: { title: 'Produits' }
            },
            {
                path: 'stock-withdrawals',
                name: 'stock-withdrawals',
                component: StockWithdrawalList,
                meta: { title: 'Retraits de stock' }
            },
            {
                path: '/products/:id',
                name: 'product-detail',
                component: ProductDetailView,
                meta: { title: 'Détails du produit' },
                props: true
            },
            {
                path: 'categories',
                name: 'categories',
                component: CategoriesView,
                meta: { title: 'Catégories' }
            },
            {
                path: 'inventory/overview',
                name: 'inventory-overview',
                component: InventoryOverview,
                meta: { title: 'État du stock' }
            },
            {
                path: 'inventory/new',
                name: 'inventory-create',
                component: CreateInventory,
                meta: { title: 'Nouvel inventaire' }
            },
            {
                path: 'inventory/physical',
                name: 'inventory-history',
                component: InventoryHistory,
                meta: { title: 'Historique inventaires' }
            },
            {
                path: 'inventory/:id',
                name: 'inventory-details',
                component: InventoryDetails,
                meta: { title: 'Détails inventaire' }
            },
            {
                path: 'product-regularizations',
                name: 'product-regularizations',
                component: ProductRegularizationsView,
                meta: { title: 'Régularisations' }
            },
            {
                path: 'create-stock-withdrawal',
                name: 'create-stock-withdrawal',
                component: CreateStockWithdrawalView,
                meta: { title: 'Nouveau retrait de stock' }
            },
            {
                path: 'stock-withdrawals/:id',
                name: 'stock-withdrawal-details',
                component: StockWithdrawalDetailsView,
                meta: { title: 'Détails du retrait de stock' }
            },

            // MODULE ACHATS & FOURNISSEURS
            {
                path: 'suppliers',
                name: 'suppliers',
                component: SuppliersView,
                meta: { title: 'Fournisseurs' }
            },
            {
                path: '/suppliers/:id',
                name: 'supplier-detail',
                component: SupplierDetailView,
                meta: { title: 'Détails fournisseur' },
                props: true
            },
            {
                path: 'purchase-orders',
                name: 'purchase-orders',
                component: PurchaseOrdersView,
                meta: { title: 'Commandes fournisseurs' }
            },
            {
                path: 'purchase-orders/new',
                name: 'purchase-orders-new',
                component: CreatePurchaseOrderView,
                meta: { title: 'Nouvelle commande' }
            },
            {
                path: 'purchase-orders/:id',
                name: 'purchase-order-details',
                component: PurchaseOrderDetailsView,
                meta: { title: 'Détails commande' },
                props: true
            },
            {
                path: 'supplier-invoices',
                name: 'supplier-invoices',
                component: SupplierInvoicesView,
                meta: { title: 'Factures fournisseurs' }
            },
            {
                path: 'suppliers/:supplierId/invoices/new',
                name: 'supplier-invoices-new',
                component: CreateSupplierInvoiceView,
                meta: { title: 'Nouvelle facture' },
                props: true
            },
            {
                path: 'supplier-invoices/:id',
                name: 'supplier-invoice-details',
                component: SupplierInvoiceDetailsView,
                meta: { title: 'Détails facture' },
                props: true
            },

            // MODULE CLIENTS
            {
                path: 'customers',
                name: 'customers',
                component: CustomerList,
                meta: { title: 'Clients' }
            },
            {
                path: '/customers/:id',
                name: 'customer-detail',
                component: CustomerDetail,
                meta: { title: 'Détails client' },
                props: true
            },

            // MODULE ADMINISTRATION
            {
                path: 'users',
                name: 'users',
                component: UsersView,
                meta: { title: 'Utilisateurs', requiresAdmin: true }
            },
            {
                path: 'notifications',
                name: 'notifications',
                component: () => import('@/views/notifications/NotificationsView.vue'),
                meta: { title: 'Notifications' }
            },

            // MODULE PARAMÈTRES
            {
                path: 'settings',
                name: 'settings',
                component: SettingsView,
                meta: { title: 'Paramètres' }
            },
            {
                path: 'profile',
                name: 'profile',
                component: ProfileView,
                meta: { title: 'Mon profil' }
            }
        ]
    },

    // Route par défaut (404)
    {
        path: '/:catchAll(.*)',
        redirect: '/dashboard'
    }
]

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior() {
        return { top: 0 }
    }
})

// Remplacer le guard de navigation existant (vers la ligne 196) par celui-ci:
router.beforeEach((to, from, next) => {
    const authStore = useAuthStore();
    const isAuthenticated = authStore.isAuthenticated;
    const isAdmin = authStore.isAdmin;
    const isMaintenanceMode = sessionStorage.getItem('maintenance_mode') === 'true';

    // Update document title
    document.title = to.meta.title ? `${to.meta.title} | SellHub` : 'SellHub';

    // Si on est en mode maintenance, bloquer toute navigation sauf vers la page de maintenance
    if (isMaintenanceMode && to.name !== 'maintenance') {
        next({ name: 'maintenance' });
        return;
    }

    // Si on n'est pas en maintenance mais qu'on essaie d'accéder à la page de maintenance, autoriser
    if (to.name === 'maintenance') {
        next();
        return;
    }

    // Si la route nécessite une authentification
    if (to.matched.some(record => record.meta.requiresAuth)) {
        if (!isAuthenticated) {
            // Rediriger vers la page de connexion
            next({
                name: 'login',
                query: { redirect: to.fullPath }
            });
        }
        // Si la route nécessite des droits d'admin
        else if (to.matched.some(record => record.meta.requiresAdmin) && !isAdmin) {
            // Rediriger vers le tableau de bord
            next({ name: 'dashboard' });
        }
        else {
            next();
        }
    }
    // Si la route est réservée aux invités (non authentifiés)
    else if (to.matched.some(record => record.meta.guest) && isAuthenticated) {
        next({ name: 'dashboard' });
    }
    else {
        next();
    }
});
export default router